/**
 * 「归入评估项」下拉(博颂 2026-09-28):选项来自成绩体系里该学科标准中对应大类
 * (考试 / 作业 / 课堂)的评估项。选中后打分自动进学期成绩的这一列。
 * 该学科还没配置标准时返回 fallback(旧的固定类型下拉)。
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { authHeaders } from "@/api/school";

export interface GradeItemOption { key: string; label: string; category: string; legacy_type: string | null; weight: number }

export function useGradeItems(classId: string | undefined, opts: { subject?: string | null; subjectId?: string | null; category: string }) {
  const q = new URLSearchParams({ class_id: classId ?? "", category: opts.category });
  if (opts.subject) q.set("subject", opts.subject);
  if (opts.subjectId) q.set("subject_id", opts.subjectId);
  return useQuery({
    queryKey: ["grade-items", classId, opts.subject ?? "", opts.subjectId ?? "", opts.category],
    queryFn: async () => {
      const r = await fetch(`/api/school/gpa/items?${q.toString()}`, { headers: authHeaders() });
      if (!r.ok) return [] as GradeItemOption[];
      return (await r.json()) as GradeItemOption[];
    },
    enabled: !!classId,
  });
}

export function GradeItemSelect({
  classId, subject, subjectId, category, value, onChange, optional, fallback, className, noneLabel, keepValue,
}: {
  classId: string | undefined;
  subject?: string | null;
  subjectId?: string | null;
  category: "exam" | "homework" | "classroom";
  value: string | null;
  onChange: (item: GradeItemOption | null) => void;
  /** 允许「不计入成绩」(课堂诊断 / 课堂记录) */
  optional?: boolean;
  fallback?: ReactNode;
  className?: string;
  /** 空选项的文字(默认「不计入学期成绩」) */
  noneLabel?: string;
  /** 编辑已有记录:不自动选中 / 重置,原值不在列表里也照样显示,不碰就不变 */
  keepValue?: boolean;
}) {
  const { t } = useTranslation();
  const items = useGradeItems(classId, { subject, subjectId, category });
  const list = items.data ?? [];
  // 默认选中第一项(必选时);已选的项不在新学科里时重置
  useEffect(() => {
    if (items.isLoading || keepValue) return;
    if (value && !list.some((i) => i.key === value)) onChange(optional ? null : list[0] ?? null);
    else if (!value && !optional && list[0]) onChange(list[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.isLoading, list.map((i) => i.key).join("|")]);
  if (!items.isLoading && list.length === 0 && !(keepValue && value)) return <>{fallback ?? null}</>;
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(list.find((i) => i.key === e.target.value) ?? null)}
      className={className ?? "w-full px-2 py-1 rounded border border-slate-200"}
      data-testid={`grade-item-${category}`}
    >
      {optional && <option value="">{noneLabel ?? t("gradeItem.none")}</option>}
      {keepValue && value && !list.some((i) => i.key === value) && <option value={value}>{value}</option>}
      {list.map((i) => (
        <option key={i.key} value={i.key}>{i.label}{i.weight ? ` · ${i.weight}%` : ""}</option>
      ))}
    </select>
  );
}
